<template>
  <sticky-header-layout>
    <template #header>
      <list-page-header>
        <div slot="right">
          <el-button
            :disabled="isLoading"
            size="mini"
            type="warning"
            icon="el-icon-close"
            @click="handleCancel"
          >{{ $t('button.cancel') }}
          </el-button>
          <el-button
            :disabled="isLoading"
            size="mini"
            type="danger"
            icon="el-icon-upload2"
            @click="handleSave"
          >{{ $t('button.save') }}
          </el-button>
        </div>
      </list-page-header>
    </template>
    <div v-loading="isLoading">
      <el-form
        ref="form"
        :disabled="isLoading"
        :model="form"
        :rules="formRules"
        class="round-border-form"
        label-position="top"
        label-width="200"
      >
        <el-form-item
          label="Store"
          prop="store_id"
        >
          <store-selector
            v-if="!isEditMode"
            v-model="form.store_id"
            :multiple="false"
            :clearable="true"
            class="w-20"
            value-key="id"
          />
          <p v-else>{{ form.store_name }}</p>
        </el-form-item>
        
        <el-form-item
          label="Title"
          prop="title"
        >
          <el-input
            v-model="form.title"
            clearable
          />
        </el-form-item>

        <el-button
          size="mini"
          type="primary"
          icon="el-icon-link"
          @click="handleOpenTranslatePage"
        >Translate Page</el-button>

        <el-form-item
          label="Translated Title"
          prop="bulk_translated_title"
        >
        <el-input
          v-model="form.bulk_translated_title"
          type="textarea"
          autosize
        />
        </el-form-item>
        
        <el-form-item
          label="Description (Ideally within 60 characters)"
          prop="description"
        >
          <el-input
            v-model="form.description"
            clearable
          />
          <div> {{ descriptionTextLengthLabel }} </div>
        </el-form-item>

        <el-button
          size="mini"
          type="primary"
          icon="el-icon-link"
          @click="handleOpenTranslatePage"
        >Translate Page</el-button>

        <el-form-item
          label="Translated Description"
          prop="bulk_translated_description"
        >
        <el-input
          v-model="form.bulk_translated_description"
          type="textarea"
          autosize
        />
        </el-form-item>

        <el-form-item
          label="Code"
          prop="code"
        >
          <el-input
            v-model="form.code"
            clearable
          />
        </el-form-item>

        <el-form-item
          label="Landing"
          prop="destination_url"
        >
          <el-input
            v-model="form.destination_url"
            clearable
          />
        </el-form-item>

        <el-form-item
          label="Discount Percentage (Put 0 if there is free shipping / $50 discount / etc.)"
          prop="discount_percentage"
        >
          <el-input-number
            v-model="form.discount_percentage"
            :min=0
            :max=100
            clearable
          />
        </el-form-item>

        <el-form-item 
          label="Evergreen"
          prop="is_evergreen"
        >
          <el-switch 
            :active-value="1"
            :inactive-value="0"
          v-model="form.is_evergreen" />
        </el-form-item>

        <el-form-item
          label="End At"
          prop="end_at"
        >
          <el-date-picker
            v-model="form.end_at"
            type="datetime"
            value-format="yyyy-MM-dd HH:mm:ss"
            placeholder="End At"
          >
          </el-date-picker>
        </el-form-item>

        <el-form-item
          label="Category"
        >
          <el-cascader
            v-model="form.categories"
            :options="categoryList" 
            :props="{
              multiple: true, checkStrictly: true, emitPath:false,
              value: 'id', label: 'text', children: 'children',
            }" 
          />
        </el-form-item>

        <el-form-item
          label="Sort (Largest number will be showing on the top)"
          prop="sort"
        >
          <el-input
            v-model="form.sort"
            clearable
          />
        </el-form-item>

        <el-form-item
          label="Featured Image (278 x 173 px)"
          prop="featured_image"
        >
          <el-upload
            v-loading="isUploadingImage"
            v-model="featured_image"
            :file-list="uploadImageFileList"
            :limit="1"
            :disabled="isUploadingImage"
            :http-request="handleUpload"
            :before-upload="handleBeforeUpload"
            :on-success="uploadOnSuccess"
            :on-exceed="uploadOnExceed"
            :on-error="uploadOnError"
            list-type="picture"
            drag
            accept=".png, .jpg"
            action="noneed"
          >
            <i class="el-icon-upload" />
            <div class="el-upload__text">Drop Image here or <em>click to upload</em></div>
          </el-upload>
        </el-form-item>

        <el-form-item
          label="Featured Sort (Largest number will be showing on the left)"
          prop="featured_sort"
        >
          <el-input
            v-model="form.featured_sort"
            clearable
          />
        </el-form-item>
      </el-form>
    </div>
  </sticky-header-layout>
</template>

<script>
import { reactive } from '@vue/composition-api'
import { getStoreList } from '../../api/stores'
import CouponService from '../../api/coupon'
import ListPageHeader from '@/views/layout/components/ListPageHeader.vue'
import { i18n } from '@/lang'
import StoreSelector from '../../components/selectors/StoreSelector.vue'
import { formatDatetime } from '../../utils/datetime'

export default {
  name: 'CouponDetail',
  components: {
    ListPageHeader,
    StoreSelector,
  },
  props: {
    id: {
      type: [String, Number],
      default: '',
    },
  },
  data() {
    const fromRule = reactive({
      store_id: [{ required: true, message: i18n.t('error.thisFieldIsRequired'), trigger: 'blur' }],
      title: [{ required: true, message: i18n.t('error.thisFieldIsRequired'), trigger: 'blur' }],
      description: [{ required: true, message: i18n.t('error.thisFieldIsRequired'), trigger: 'blur' }],
      // code: [{ required: true, message: i18n.t('error.thisFieldIsRequired'), trigger: 'blur' }],
      destination_url: [{ type:'url', required: true, message: i18n.t('error.thisFieldMustBeAnUrl'), trigger: 'blur' }],
      discount_percentage: [{ type:'integer', required: true, message: i18n.t('error.thisFieldMustBePositiveInteger'), trigger: 'blur' }],
      sort: [{ required: true, message: i18n.t('error.thisFieldIsRequired'), trigger: 'blur' }],
    })

    return {
      categoryList: [],
      storeList: [],
      uploadImageFileList: [],
      featured_image: '',
      isUploadingImage: false,
      isLoading: true,
      form: {
        store_id: '',
        store_name: '',
        title: '',
        bulk_translated_title: '',
        description: '',
        bulk_translated_description: '',
        code: '',
        destination_url: '',
        discount_percentage: 0,
        end_at: '',
        is_evergreen: 0,
        categories: [],
        sort: 0,
        featured_sort: 0,
      },
      formRules: fromRule,
      descriptionTextLength: 0,
    }
  },
  computed: {
    isEditMode() {
      return !!this.id
    },
    canSave() {
      return !this.loading
    },
    descriptionTextLengthLabel() {
      return `Length: ${this.descriptionTextLength}`
    }
  },
  watch: {
    'form.description': function _(value) {
      this.descriptionTextLength = value.length
    },
  },
  mounted() {
    if (this.isEditMode) {
      this.getDetail(this.id) 
    }

    this.getCategoryList()
  },
  methods: {
    formatDatetimeToUTC(date) {
      return formatDatetime({ date: date, timezoneOffset: -8})
    },
    async getDetail() {
      this.isLoading = true
      try {
        const response = await CouponService.getDetail(this.id)
        const { data } = response

        this.form = {
          store_id: data.store_id,
          store_name: data.store_name,
          title: data.title,
          description: data.description,
          code: data.code,
          destination_url: data.destination_url,
          discount_percentage: data.discount_percentage,
          bulk_translated_description: data.bulk_translated_description,
          bulk_translated_title: data.bulk_translated_title,
          categories: data.categories,
          is_evergreen: data.is_evergreen,
          end_at: data.end_at ? formatDatetime({date: data.end_at}) : '',
          sort: data.sort,
          featured_sort: data.featured_sort,
        }

        if (data.featured_image) {
          this.uploadImageFileList.push({
            name: '',
            url: data.featured_image,
          })
        }
        
        this.isLoading = false
      } catch (error) {
        this.isLoading = false
      }
    },
    async getStoreList() {
      this.isLoading = true
      this.storeList = []

      try {
        const response = await getStoreList('all')
        
        if (Array.isArray(response.data)) {
          var data = response.data
          var result = data.reduce((unique, o) => {
            console.log(o)
            if(!unique.some(obj => obj.id === o.id)) {
              unique.push(o);
            }
            return unique;
          },[]);

          this.storeList = result
        }

        this.isLoading = false
      } catch (error) {
        this.isLoading = false
      }
    },
    async getCategoryList() {
      this.isLoading = true
      this.categoryList = []

      try {
        const response = await CouponService.getCategoryList()
        if (Array.isArray(response.data)) {
          var data = response.data
          this.categoryList = data
        }

        this.isLoading = false
      } catch (error) {
        this.isLoading = false
      }
    },
    async handleSave() {
      try {
        if (this.form.end_at) {
          this.form.end_at = this.formatDatetimeToUTC(this.form.end_at)
        }

        await this.$refs.form.validate()

        if (this.isEditMode) {
          this.editCoupon(this.id, this.form)
        } else {
          this.createCoupon(this.form)
        }
        // console.log(this.form)
      } catch (error) {
        this.$alert('Please fill in all the required information.', 'Oops..', {
          confirmButtonText: 'OK',
        })
      }
    },
    handleCancel() {
      this.$router.push({ path: `/coupon/list` })
    },
    async handleUpload(upload) {
      this.isUploading = true

      try {
        this.featured_image = upload.file
        upload.onSuccess()
      } catch (error) {
        upload.onError(error)
      }
    },
    uploadOnSuccess() {
      this.isUploading = false
    },
    uploadOnError(error) {
      console.log(error)
      this.isUploading = false
      this.$alert(
        'Please try again later.',
        'Upload Failed', {
          type: 'error',
          confirmButtonText: 'OK',
        },
      )
    },
    uploadOnExceed() {
      this.$alert(
        'Sorry, please upload one file at a time!',
        'Too Many Files', {
          type: 'error',
          confirmButtonText: 'OK',
        },
      )
    },
    handleBeforeUpload(file) {
      if (!file.name.includes('.png') && !file.name.includes('.jpg') && !file.name.includes('.jpeg')) {
        this.$alert(
          'Please double check you are uploading a .png, .jpg, or .jpeg file',
          'Incorrect File Type', {
            type: 'error',
            confirmButtonText: 'OK',
          },
        )
        return false
      }

      return true
    },
    async createCoupon(form) {
      this.isLoading = true
      try {
        const result = await CouponService.create(form, this.featured_image)
        this.handleSaveSuccess()
      } catch (error) {
        this.handleSaveFail(error)
      }
    },
    async editCoupon(id, form) {
      this.isLoading = true
      try {
        const result = await CouponService.update(id, form, this.featured_image)
        this.handleSaveSuccess()
      } catch (error) {
        this.handleSaveFail(error)
      }
    },
    handleSaveSuccess() {
      this.isLoading = false
      this.$router.push({ path: `/coupon/list` })
    },
    handleSaveFail(error) {
      this.isLoading = false
      this.$alert(`Error: ${error}`, this.$t('error.somethingWentWrong'), {
        type: 'error',
        confirmButtonText: 'OK',
      })
    },
    handleOpenTranslatePage() {
      window.open("https://docs.google.com/spreadsheets/d/1-7gkf4vCLga-pzlxNY18g_UPbR2GCz-scKiir0z6xJc/edit#gid=0", '_blank');
    },
  },
}
</script>

<style lang="scss" scoped>
@import "~@/assets/styles/view-base.scss";
@import "~@/assets/styles/form.scss";
@import "~@/assets/styles/table.scss";
p {
  margin: 0 10px; 
}

.el-upload-list__item-thumbnail {
  width: 200px;
  height: auto;
}
</style>

